import React, { useEffect, useMemo, useState } from 'react';
import { Icon } from '@iconify/react';
import { useQuery, useQueryClient, useIsFetching, useQueries } from '@tanstack/react-query';
import { NewCampaignButton } from '../atoms/NewCampaignButton';
import { useAuth } from '../../contexts/AuthContext';
import { useTeamPreferences } from '../../hooks/useTeamPreferences';
import { teamInviteService } from '../../services/backend/appwrite/teamInviteService';
import { normalizeSellerOptions } from '../modules/CampaignCreator/utils/sellerOptions';
import { parseSellerReference } from '../../utils/sellerReference';
import { buildSellerProfile, getSellerEnrichedAt } from '../../utils/sellerProfile';
import { queryKeys } from '../../services/query/queryKeys';
import { AdminCampaignBoard } from './AdminCampaignBoard';
import { InviteTeamMemberModal } from './InviteTeamMemberModal';
import { TeamDirectory } from './atoms/TeamDirectory';
import { fetchSellerProfile, SELLER_PROFILE_GC_TIME, SELLER_PROFILE_STALE_TIME } from '../../hooks/useSellerProfile';
import { sellerCampaignCounts } from './sellerCampaignCounts';
import { SellerCampaignCounts } from './atoms/SellerCampaignCounts';
import { SellerList } from './atoms/SellerList';
import { SellerCard } from './atoms/SellerCard';
import { useSellerActions } from './SellerActions';
import { AddSellerModal } from './AddSellerModal';
import { OutreachCalculator } from './OutreachCalculator';
import type { CampaignMetadata } from '../../types';
import type { TeamInvitationData, TeamMemberData } from '../../types/n8nResponses';
import './teamWorkspace.css';

export type TeamTab = 'campaigns' | 'members' | 'sellers';
const TABS = [
  { id: 'campaigns', label: 'Team Campaigns', icon: 'solar:chart-square-bold' },
  { id: 'members', label: 'Team Members', icon: 'solar:users-group-rounded-bold' },
  { id: 'sellers', label: 'Sellers', icon: 'solar:buildings-2-linear' },
] as const;

/** Sort keys for the Sellers grid. `added` = registry order (the seller_ids
 *  CSV has no timestamp — insertion order IS creation order). Enriched reads
 *  the profile cache (already fetched for the card logos). */
type SellerSort = 'added' | 'name' | 'enriched-desc' | 'enriched-asc' | 'opportunities' | 'industry' | 'location' | 'employees';
const SELLER_SORTS: Array<{ id: SellerSort; label: string }> = [
  { id: 'added', label: 'Date added' },
  { id: 'name', label: 'Name (A–Z)' },
  { id: 'opportunities', label: 'Most opportunities' },
  { id: 'industry', label: 'Industry' },
  { id: 'location', label: 'Location' },
  { id: 'employees', label: 'Most employees' },
  { id: 'enriched-desc', label: 'Recently enriched' },
  { id: 'enriched-asc', label: 'Oldest enrichment' },
];

export const TeamWorkspace: React.FC<{
  campaigns: CampaignMetadata[]; teamMembers: TeamMemberData[]; teamInvitations: TeamInvitationData[];
  isLoading: boolean; error?: string | null; onRetry?: () => void;
  activeTab: TeamTab; onTabChange: (tab: TeamTab) => void; onOpenCampaign?: (id: string) => void;
  onAddCampaign?: () => void;
  /** Team's current AI-credit balance — shown as the weekly runway pill. */
  availableCredits?: number | null;
  /** Opens the Buy-Credits modal — surfaced in the pill when coverage runs low. */
  onAddCredits?: () => void;
  /** Team name — fallback "seller" for campaigns without a seller assigned. */
  teamName?: string | null;
  /** Team's `id§Name` seller registry — resolves bare campaign seller IDs. */
  sellerRegistry?: string | null;
  /** Opens the Report Generator locked to a campaign (card "+Report"). */
  onGenerateReport?: (campaignId: string) => void;
}> = ({ campaigns, teamMembers, teamInvitations, isLoading, error, onRetry, activeTab, onTabChange, onOpenCampaign, onAddCampaign, availableCredits, onAddCredits, teamName, sellerRegistry, onGenerateReport }) => {
  const { currentUser, targetEmail } = useAuth();
  const email = targetEmail || currentUser?.email || '';
  const queryClient = useQueryClient();
  const preferences = useTeamPreferences();
  const [inviteOpen, setInviteOpen] = useState(false);
  const [sellerSearch, setSellerSearch] = useState('');
  const [sellerSort, setSellerSort] = useState<SellerSort>('added');
  const [sellerView, setSellerView] = useState<'cards' | 'list'>('cards');
  const [sortDescending, setSortDescending] = useState(false);
  useEffect(() => {
    try { setSellerView(localStorage.getItem(`sellers-view:${email}`) === 'list' ? 'list' : 'cards'); } catch { setSellerView('cards'); }
  }, [email]);
  const changeSellerView = (view: 'cards' | 'list') => {
    setSellerView(view);
    try { localStorage.setItem(`sellers-view:${email}`, view); } catch { /* Storage can be unavailable. */ }
  };
  const changeSort = (sort: SellerSort) => {
    setSortDescending(sort === sellerSort ? !sortDescending : false);
    setSellerSort(sort);
  };
  const [addSellerOpen, setAddSellerOpen] = useState(false);
  const sellerActions = useSellerActions({
    onRemoved: () => void preferences.refetch(),
  });
  const roster = useQuery({
    queryKey: ['team-roster', email],
    queryFn: () => teamInviteService.getTeamRoster({ admin_email: email }),
    enabled: Boolean(email),
    staleTime: 30_000,
    refetchInterval: activeTab === 'members' ? 30_000 : false,
    refetchOnWindowFocus: true,
    retry: 1,
  });
  const members = roster.data?.data?.team_members ?? teamMembers;
  const invitations = roster.data?.data?.team_invitations ?? teamInvitations;
  // Any seller-profile query (one per card) currently fetching? Reactive via
  // the query cache — no duplicate observers needed.
  const sellerProfilesFetching = useIsFetching({ queryKey: queryKeys.sellers.profileRoot });
  const sellers = useMemo(() => {
    const registry = normalizeSellerOptions(preferences.data?.seller_ids);
    const unique = new Map(registry.map((seller) => [parseSellerReference(seller.value)!.id, seller]));
    const list = Array.from(unique, ([id, seller]) => ({ ...seller, id }));
    // The team itself is the DEFAULT seller (it already has a lvl1 profile —
    // prefs.lvl1_id — and is the context used when a campaign has no seller).
    // Show it first so the Sellers tab is never empty for a new team.
    const teamLvl1Id = preferences.data?.lvl1_id?.trim();
    if (teamLvl1Id && !unique.has(teamLvl1Id)) {
      list.unshift({
        id: teamLvl1Id,
        value: `${teamLvl1Id}§${preferences.data?.team_name || 'My team'}`,
        label: preferences.data?.team_name || 'My team',
      });
    }
    return list;
  }, [preferences.data?.seller_ids, preferences.data?.lvl1_id, preferences.data?.team_name]);
  // Subscribe to the shared cache so profile-based sorting updates as enrichment arrives.
  const profiles = useQueries({ queries: sellers.map(seller => ({
    queryKey: queryKeys.sellers.profile(seller.id), queryFn: fetchSellerProfile(seller),
    enabled: activeTab === 'sellers',
    staleTime: (query) => getSellerEnrichedAt(query.state.data?.data) ? SELLER_PROFILE_STALE_TIME : 0, gcTime: SELLER_PROFILE_GC_TIME, retry: 1,
    select: (response: Awaited<ReturnType<ReturnType<typeof fetchSellerProfile>>>) => buildSellerProfile(response.data, seller),
  })) });
  const profileMap = new Map(sellers.map((seller, i) => [seller.id, profiles[i]]));
  const filteredSellers = sellers.filter(seller => `${seller.label} ${seller.id}`.toLowerCase().includes(sellerSearch.trim().toLowerCase()));
  const field = (id: string) => {
    const p = profileMap.get(id)?.data;
    if (sellerSort === 'opportunities') return -(p?.opportunities.length ?? -1);
    if (sellerSort === 'employees') return -Number(p?.facts.find(f => f.label === 'Employees')?.value.replace(/,/g, '') || 0);
    if (sellerSort === 'industry') return p?.industry || '';
    if (sellerSort === 'location') return p?.location || '';
    return p?.enrichedAt || '';
  };
  if (sellerSort !== 'added') filteredSellers.sort((a,b) => {
    const av = sellerSort === 'name' ? a.label : field(a.id), bv = sellerSort === 'name' ? b.label : field(b.id);
    const comparison = typeof av === 'number' && typeof bv === 'number' ? av - bv : String(av).localeCompare(String(bv));
    return comparison * (sellerSort === 'enriched-desc' ? -1 : 1) * (sortDescending ? -1 : 1) || a.label.localeCompare(b.label);
  });
  const campaignCounts = useMemo(() => sellerCampaignCounts(campaigns, sellers, preferences.data?.lvl1_id?.trim()), [campaigns, sellers, preferences.data?.lvl1_id]);
  const counts = { campaigns: isLoading ? '—' : campaigns.length, members: roster.isPending ? '—' : members.length + invitations.filter((item) => item.status === 'pending').length, sellers: preferences.isPending ? '—' : sellers.length };

  return (
    <div className="team-workspace">
      <div className="team-nav-header">
        <nav aria-label="Team sections" className="team-nav" role="tablist">
          {TABS.map((tab, index) => (
            <button key={tab.id} id={`team-tab-${tab.id}`} type="button" role="tab"
              aria-selected={activeTab === tab.id} aria-controls={`team-panel-${tab.id}`}
              tabIndex={activeTab === tab.id ? 0 : -1}
              onClick={() => onTabChange(tab.id)}
              onKeyDown={(event) => {
                const nextIndex = event.key === 'ArrowRight' ? (index + 1) % TABS.length
                  : event.key === 'ArrowLeft' ? (index + TABS.length - 1) % TABS.length
                    : event.key === 'Home' ? 0 : event.key === 'End' ? TABS.length - 1 : -1;
                if (nextIndex < 0) return;
                event.preventDefault();
                onTabChange(TABS[nextIndex].id);
                document.getElementById(`team-tab-${TABS[nextIndex].id}`)?.focus();
              }}>
              <Icon icon={tab.icon} className="h-4 w-4 shrink-0" aria-hidden="true" />
              <span>{tab.label}</span><span className="team-nav-count">{counts[tab.id]}</span>
            </button>
          ))}
        </nav>
        <OutreachCalculator requestContext={{
          userEmail: currentUser?.email,
          userName: currentUser?.displayName,
          impersonatedEmail: targetEmail,
          clientInfo: preferences.data ? {
            id: preferences.data.lvl1_id || '',
            name: preferences.data.team_name || '',
            email: preferences.data.billing_email || email,
            hasOutreachModule: preferences.data.modules.outreach,
          } : null,
        }} />
        {activeTab === 'sellers' && <>
          {/* Refresh forces FRESH seller profiles: the ['sellers'] cache is
              persistent and never invalidated on boot, so this explicit
              invalidate is the only refetch trigger. */}
          <button type="button" className="team-action" aria-label="Refresh sellers"
            disabled={preferences.isFetching || sellerProfilesFetching > 0}
            onClick={() => { void preferences.refetch(); void queryClient.invalidateQueries({ queryKey: queryKeys.sellers.profileRoot }); }}>
            <Icon icon="solar:refresh-bold" className={preferences.isFetching || sellerProfilesFetching > 0 ? 'animate-spin' : ''} aria-hidden="true" />
          </button>
          <button
            type="button"
            className="inline-flex h-10 shrink-0 items-center gap-2 rounded-[14px] bg-white px-4 text-[11px] font-black uppercase tracking-[0.12em] text-[#0B0B0D] shadow-[0_10px_24px_rgba(255,255,255,0.08)] transition-all hover:bg-[#e9ecff]"
            onClick={() => setAddSellerOpen(true)}
          >
            <Icon icon="solar:buildings-2-bold" className="h-4 w-4" aria-hidden="true" />
            Add seller
          </button>
        </>}
        {activeTab === 'campaigns' && onAddCampaign && (
          <NewCampaignButton size="sm" onClick={onAddCampaign} className="hidden sm:inline-flex" />
        )}
      </div>
      <div key={activeTab} id={`team-panel-${activeTab}`} role="tabpanel" aria-labelledby={`team-tab-${activeTab}`} tabIndex={0} className="team-panel">
        {activeTab === 'campaigns' && (error && campaigns.length === 0 ? <div role="alert" className="team-section-header text-rose-200">{error}<button type="button" className="team-action ml-3" onClick={onRetry}>Retry</button></div> :
          <>
            {/* TanStack keeps cached data after a failed refetch — show the stale
                board with a dismissible-feel warning instead of replacing it with
                a full error state. */}
            {error && (
              <div role="alert" className="mb-3 flex flex-wrap items-center gap-3 rounded-2xl border border-amber-300/16 bg-amber-300/10 px-4 py-3 text-sm text-amber-100">
                <Icon icon="solar:danger-triangle-bold" className="h-4 w-4 shrink-0" aria-hidden="true" />
                <span>Campaigns shown may be out of date — the last refresh failed.</span>
                <button type="button" className="team-action" onClick={onRetry}>Retry</button>
              </div>
            )}
            <AdminCampaignBoard campaigns={campaigns} teamMembers={members} teamInvitations={invitations} isLoading={isLoading}
              onOpenCampaign={onOpenCampaign} onViewMembers={() => onTabChange('members')} onViewSellers={() => onTabChange('sellers')}
              sellerCount={preferences.isPending || preferences.isError ? undefined : sellers.length}
              availableCredits={availableCredits}
              onAddCredits={onAddCredits}
              teamName={preferences.data?.team_name || null}
              sellerRegistry={preferences.data?.seller_ids || null}
              onGenerateReport={onGenerateReport}
              userEmail={email} />
          </>)}
        {activeTab === 'members' && <>
          <header className="team-list-heading">
            <div><h2>Team members <span>{counts.members}</span></h2><p className="team-directory-description">The people in your workspace. Manage access and invitations.</p></div>
            <div className="flex items-center gap-2">
              <button type="button" className="team-action" aria-label="Refresh members" disabled={roster.isFetching} onClick={() => void roster.refetch()}><Icon icon="solar:refresh-bold" className={roster.isFetching ? 'animate-spin' : ''} aria-hidden="true" /></button>
              <button type="button" className="team-directory-report team-directory-invite" onClick={() => setInviteOpen(true)}><Icon icon="solar:user-plus-bold" aria-hidden="true" />Invite member</button>
            </div>
          </header>
          {roster.isError && <p role="alert" className="my-3 text-sm text-amber-200">Could not refresh team access. The last available roster is shown. Use Refresh to try again.</p>}
          {roster.isPending && <p role="status" className="py-4 text-sm text-white/60">Loading team access…</p>}
          <div className="mt-4"><InviteTeamMemberModal embedded isOpen onClose={() => {}} teamMembers={members} teamInvitations={invitations} /></div>
        </>}
        {activeTab === 'sellers' && <div className="team-member-directory">
          <div className="team-directory-toolbar team-seller-toolbar">
            <div className="team-directory-search">
              <Icon icon="bx:search" aria-hidden="true" />
              <input
                type="search"
                aria-label="Search sellers"
                placeholder="Search sellers…"
                className="team-search"
                value={sellerSearch}
                onChange={(event) => setSellerSearch(event.target.value)}
              />
            </div>
            <label className="team-seller-sort">
              <Icon icon="bx:sort" aria-hidden="true" />
              <select aria-label="Sort sellers" className="team-directory-select" value={sellerSort}
                onChange={(event) => { setSellerSort(event.target.value as SellerSort); setSortDescending(false); }}>
                {SELLER_SORTS.map((sort) => <option key={sort.id} value={sort.id}>Sort: {sort.label}</option>)}
              </select>
            </label>
            <span className="team-directory-count" role="status">{preferences.isPending ? '' : `${filteredSellers.length} of ${sellers.length} sellers`}</span>
            <div className="seller-view-toggle" role="group" aria-label="Seller view">
              {(['cards', 'list'] as const).map(view => <button key={view} type="button" aria-pressed={sellerView === view} onClick={() => changeSellerView(view)}>
                <Icon icon={view === 'cards' ? 'solar:widget-linear' : 'solar:list-linear'} aria-hidden="true" />{view === 'cards' ? 'Cards' : 'List'}
              </button>)}
            </div>

          </div>
          {sellerActions.removeError && <p role="alert" className="mt-3 text-sm text-rose-300">{sellerActions.removeError}</p>}
          <div className={sellerView === 'cards' ? 'team-seller-cards-content' : undefined}>
            {preferences.isPending ? <p role="status" className="py-8 text-center text-white/60">Loading sellers…</p>
              : preferences.isError ? <p role="alert" className="py-8 text-center text-amber-200">Could not load sellers. Use Refresh to try again.</p>
              : filteredSellers.length === 0 ? <div className="team-directory-empty">
                  <Icon icon="solar:buildings-2-linear" aria-hidden="true" />
                  <h3>{sellerSearch ? 'No matching sellers' : 'No sellers added yet'}</h3>
                  <p>{sellerSearch ? 'Try another name or ID.' : 'Use "Add seller" to register your first seller.'}</p>
                </div>
              : sellerView === 'list' ? <SellerList sellers={filteredSellers} campaignCounts={campaignCounts} campaignsLoading={isLoading} campaignsError={Boolean(error)} profiles={profileMap} onOpen={sellerActions.openDetails} sort={sellerSort} descending={sortDescending} onSort={changeSort} />
              : <ul className="team-seller-grid" aria-label="Sellers">
                  {filteredSellers.map((seller) => (
                    <li key={seller.id}>
                      <SellerCard
                        seller={seller}
                        campaignCounts={<SellerCampaignCounts counts={campaignCounts.get(seller.id)} loading={isLoading} error={Boolean(error)} />}
                        actions={{
                          onOpen: sellerActions.openDetails,
                          onRemove: (target) => void sellerActions.remove(target),
                          removingId: sellerActions.removingId,
                        }}
                      />
                    </li>
                  ))}
                </ul>}
          </div>
        </div>}
      </div>
      <InviteTeamMemberModal isOpen={inviteOpen} onClose={() => setInviteOpen(false)} teamMembers={members} teamInvitations={invitations} />
      {sellerActions.modals}
      {addSellerOpen && (
        <AddSellerModal
          teamName={preferences.data?.team_name || email}
          clientEmail={email}
          onClose={() => setAddSellerOpen(false)}
          onCreated={() => {
            setAddSellerOpen(false);
            void preferences.refetch();
          }}
        />
      )}
    </div>
  );
};
